import { useQueryClient } from '@tanstack/react-query';
import { 
  useCommentsServiceGetApiCommentsMy,
  useCommentsServicePostApiComments,
  useCommentsServicePutApiCommentsById,
  useCommentsServiceDeleteApiCommentsById
} from '../api/generated/queries/queries';
import { CommentDTO } from '../api/generated/requests/types.gen';
import { useSnackbar } from 'notistack';

// Local Note interface (current frontend structure)
export interface Note {
    id: string;
    content: string;
    createdAt: string;
}

/**
 * Hook do pobierania komentarzy użytkownika z cache'owaniem
 * Używa React Query do cache'owania danych
 */
export const useMyComments = () => {
  return useCommentsServiceGetApiCommentsMy([], {
    staleTime: 0, // Zawsze wykonuj request w tle, ale serwuj dane z cache od razu
    gcTime: 30 * 60 * 1000, // 30 minut w cache
    refetchOnWindowFocus: false, // Nie odświeżaj automatycznie przy focus
    refetchOnMount: true, // Zawsze odśwież przy mount komponentu
    enabled: true, // Explicitly enable the query
  });
};

/**
 * Hook do zarządzania danymi komentarzy/notatek z automatycznym cache'owaniem i aktualizacją
 */
export const useCommentData = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Pobierz dane z cache'owanego hooka
  const commentsQuery = useMyComments();

  // Special values for standalone notes
  const NOTES_OBJECT_ID = 0;
  const NOTES_OBJECT_TYPE = 'TASK' as const; // Using TASK as it's available in the enum

  /**
   * Convert CommentDTO from backend to frontend Note interface
   */
  const commentToNote = (comment: CommentDTO): Note => {
    return {
      id: comment.id?.toString() || '',
      content: comment.description || '',
      createdAt: comment.createdAt || new Date().toISOString(),
    };
  };

  /**
   * Convert frontend Note to CommentDTO for backend
   */
  const noteToComment = (note: Partial<Note>): CommentDTO => {
    return {
      id: note.id ? parseInt(note.id) : undefined,
      description: note.content || '',
      objectId: NOTES_OBJECT_ID,
      objectType: NOTES_OBJECT_TYPE,
      createdAt: note.createdAt,
    };
  };

  // Filtrowanie komentarzy do notatek (objectId = 0)
  const notes = (commentsQuery.data || [])
    .filter(comment => 
      comment.objectId === NOTES_OBJECT_ID && 
      comment.objectType === NOTES_OBJECT_TYPE
    )
    .map(commentToNote);

  // Funkcja do odświeżania danych komentarzy
  const refreshComments = async () => {
    await queryClient.invalidateQueries({ queryKey: ['CommentsServiceGetApiCommentsMy'] });
  };

  // Funkcja do aktualizacji komentarza w cache bez ponownego pobierania
  const updateCommentInCache = (commentId: number, updates: Partial<CommentDTO>) => {
    queryClient.setQueryData(['CommentsServiceGetApiCommentsMy'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.map((comment: any) => 
        comment.id === commentId ? { ...comment, ...updates } : comment
      );
    });
  };

  // Funkcja do dodania komentarza do cache
  const addCommentToCache = (newComment: CommentDTO) => {
    queryClient.setQueryData(['CommentsServiceGetApiCommentsMy'], (oldData: any) => {
      if (!oldData) return [newComment];
      return [newComment, ...oldData];
    });
  };

  // Funkcja do usunięcia komentarza z cache
  const removeCommentFromCache = (commentId: number) => {
    queryClient.setQueryData(['CommentsServiceGetApiCommentsMy'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter((comment: any) => comment.id !== commentId);
    });
  };

  return {
    // Dane
    notes,
    comments: commentsQuery.data || [],

    // Stany ładowania
    isLoading: commentsQuery.isLoading,

    // Stany błędów
    error: commentsQuery.error,

    // Funkcje pomocnicze
    refreshComments,
    updateCommentInCache,
    addCommentToCache,
    removeCommentFromCache,
    commentToNote,
    noteToComment,

    // Oryginalne query object dla zaawansowanego użycia
    commentsQuery
  };
};

/**
 * Hook do mutacji komentarzy z automatyczną aktualizacją cache
 */
export const useCommentMutations = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Funkcja do aktualizacji komentarza w cache bez ponownego pobierania
  const updateCommentInCache = (commentId: number, updates: Partial<CommentDTO>) => {
    queryClient.setQueryData(['CommentsServiceGetApiCommentsMy'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.map((comment: any) => 
        comment.id === commentId ? { ...comment, ...updates } : comment
      );
    });
  };

  // Funkcja do dodania komentarza do cache
  const addCommentToCache = (newComment: CommentDTO) => {
    queryClient.setQueryData(['CommentsServiceGetApiCommentsMy'], (oldData: any) => {
      if (!oldData) return [newComment];
      return [newComment, ...oldData];
    });
  };

  // Funkcja do usunięcia komentarza z cache
  const removeCommentFromCache = (commentId: number) => {
    queryClient.setQueryData(['CommentsServiceGetApiCommentsMy'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter((comment: any) => comment.id !== commentId);
    });
  };

  // Mutation do tworzenia komentarza
  const createCommentMutation = useCommentsServicePostApiComments({
    onSuccess: (data) => {
      addCommentToCache(data);
      enqueueSnackbar('Komentarz został utworzony', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Błąd podczas tworzenia komentarza:', error);
      enqueueSnackbar('Nie udało się utworzyć komentarza', { variant: 'error' });
    }
  });

  // Mutation do aktualizacji komentarza z optymistyczną aktualizacją
  const updateCommentMutation = useCommentsServicePutApiCommentsById({
    onMutate: async (variables) => {
      // Anuluj wszystkie wychodzące zapytania dla komentarzy
      await queryClient.cancelQueries({ queryKey: ['CommentsServiceGetApiCommentsMy'] });

      // Zapisz poprzednie dane na wypadek rollback
      const previousComments = queryClient.getQueryData(['CommentsServiceGetApiCommentsMy']);

      // Optymistycznie zaktualizuj cache
      updateCommentInCache(variables.id, variables.requestBody);

      // Zwróć kontekst z poprzednimi danymi
      return { previousComments };
    },
    onSuccess: (data, variables) => {
      // Zaktualizuj cache z rzeczywistymi danymi z serwera
      updateCommentInCache(variables.id, data);
      enqueueSnackbar('Komentarz został zaktualizowany', { variant: 'success' });
    },
    onError: (error, variables, context) => {
      // Przywróć poprzednie dane w przypadku błędu
      if (context?.previousComments) {
        queryClient.setQueryData(['CommentsServiceGetApiCommentsMy'], context.previousComments);
      }

      console.error('Błąd podczas aktualizacji komentarza:', error);
      enqueueSnackbar('Nie udało się zaktualizować komentarza', { variant: 'error' });
    },
    onSettled: () => {
      // Zawsze odśwież dane po zakończeniu mutacji (sukces lub błąd)
      queryClient.invalidateQueries({ queryKey: ['CommentsServiceGetApiCommentsMy'] });
    }
  });

  // Mutation do usuwania komentarza
  const deleteCommentMutation = useCommentsServiceDeleteApiCommentsById({
    onSuccess: (data, variables) => {
      removeCommentFromCache(variables.id);
      enqueueSnackbar('Komentarz został usunięty', { variant: 'success' });
    },
    onError: (error) => {
      console.error('Błąd podczas usuwania komentarza:', error);
      enqueueSnackbar('Nie udało się usunąć komentarza', { variant: 'error' });
    }
  });

  return {
    createComment: createCommentMutation.mutate,
    updateComment: updateCommentMutation.mutate,
    deleteComment: deleteCommentMutation.mutate,

    // Stany mutacji
    isCreating: createCommentMutation.isPending,
    isUpdating: updateCommentMutation.isPending,
    isDeleting: deleteCommentMutation.isPending,
  };
};
